import { useState } from "react";
import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import FAQ from "@/components/FAQ";
import { submitMailto } from "@/lib/mailto";
import { MapPin, Clock, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";

const CONTACT_FAQ = [
  { q: "How fast do you respond?", a: "We reply to demo requests within one business day. Support responds within 4 business hours." },
  { q: "Do you support in-person demos?", a: "Yes. For Enterprise conversations we can travel. Reach out via the contact form." },
  { q: "How can I report a security issue?", a: "Please use the contact form and mark your message as a security disclosure. We take security disclosures seriously." },
];

export default function Contact() {
  const [state, setState] = useState({ name: "", email: "", company: "", message: "" });
  const onChange = (e) => setState((s) => ({ ...s, [e.target.name]: e.target.value }));
  const onSubmit = (e) => {
    e.preventDefault();
    const body = `Name: ${state.name}\nEmail: ${state.email}\nCompany: ${state.company}\n\nMessage:\n${state.message}`;
    submitMailto({
      to: "sales@rxn3d.com",
      subject: `[Rxn3D · Contact] ${state.name || "New inquiry"}`,
      body,
      testLabel: "contact",
    });
  };

  return (
    <>
      <PageHero
        chapter="§ 10"
        eyebrow="Contact"
        title={"Let's talk about your lab."}
        description="Whether you're evaluating Rxn3D or already on board, we'd love to hear from you."
      />

      <section data-testid="contact-grid" className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-3"
          >
            {/* <ContactInfo icon={Mail} label="Sales" value="sales@rxn3d.com" href="mailto:sales@rxn3d.com" testId="contact-sales" />
            <ContactInfo icon={Mail} label="Support" value="support@rxn3d.com" href="mailto:support@rxn3d.com" testId="contact-support" /> */}
            {/* <ContactInfo icon={Phone} label="Phone" value="+1 (555) 010-0000" href="tel:+15550100000" testId="contact-phone" /> */}
            <ContactInfo icon={MapPin} label="Address" value="Rxn3D LLC, 930 S 4th St, Ste 209 #5749, Las Vegas, NV 89101" testId="contact-address" />
            <ContactInfo icon={Clock} label="Hours" value="Mon to Fri, 9:00 to 18:00 (PT)" testId="contact-hours" />

            <div className="rounded-3xl hairline glass p-6 grain relative overflow-hidden">
              <div className="absolute inset-0 opacity-30 pointer-events-none">
                <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-brand-gradient blur-3xl opacity-70" />
              </div>
              <div className="relative">
                <div className="text-xs text-muted-foreground">Live map</div>
                <div className="font-heading text-lg font-semibold mt-1">Las Vegas HQ</div>
                <div className="mt-4 aspect-video rounded-2xl overflow-hidden hairline">
                  <svg viewBox="0 0 400 220" className="w-full h-full">
                    <rect width="400" height="220" className="fill-black/[0.03] dark:fill-white/[0.04]" />
                    <g opacity="0.4" className="stroke-foreground/30">
                      {Array.from({ length: 10 }).map((_, i) => (
                        <line key={i} x1={i * 40} x2={i * 40} y1={0} y2={220} strokeWidth="1" />
                      ))}
                      {Array.from({ length: 6 }).map((_, i) => (
                        <line key={i} x1={0} x2={400} y1={i * 40} y2={i * 40} strokeWidth="1" />
                      ))}
                    </g>
                    <path d="M50,180 C120,140 160,80 220,90 C260,96 300,60 360,80" stroke="url(#brand-g-map)" strokeWidth="3" fill="none" strokeLinecap="round" />
                    <defs>
                      <linearGradient id="brand-g-map" x1="0" x2="1"><stop offset="0" stopColor="#2AA6DE"/><stop offset="0.6" stopColor="#82298D"/><stop offset="1" stopColor="#C9539F"/></linearGradient>
                    </defs>
                    <circle cx="220" cy="90" r="7" fill="url(#brand-g-map)" />
                    <circle cx="220" cy="90" r="14" fill="none" stroke="url(#brand-g-map)" strokeWidth="2" opacity="0.4" />
                  </svg>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7 rounded-3xl hairline glass p-8 lg:p-10"
          >
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground mb-4">
              Send a message
            </div>
            <h3 className="font-heading text-3xl sm:text-4xl font-semibold tracking-[-0.02em]">
              We'd love to hear from you.
            </h3>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Name" name="name" value={state.name} onChange={onChange} required />
              <Field label="Email" name="email" type="email" value={state.email} onChange={onChange} required />
              <Field label="Company" name="company" value={state.company} onChange={onChange} className="sm:col-span-2" />
              <Field
                label="Message"
                name="message"
                as="textarea"
                value={state.message}
                onChange={onChange}
                className="sm:col-span-2"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              className="mt-8 group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              Send message
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>
        </div>
      </section>

      <FAQ items={CONTACT_FAQ} title="Contact FAQ" />
      <CTA />
    </>
  );
}

function ContactInfo({ icon: Icon, label, value, href, testId }) {
  const inner = (
    <div className="rounded-2xl hairline glass p-5 flex items-center gap-4">
      <div className="h-10 w-10 rounded-xl hairline inline-flex items-center justify-center">
        <Icon className="h-4 w-4 text-brand-plum" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-mono text-muted-foreground">
          {label}
        </div>
        <div className="text-sm font-medium">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block hover:opacity-90">{inner}</a>
  ) : (
    <div data-testid={testId}>{inner}</div>
  );
}

function Field({ label, name, value, onChange, type = "text", as = "input", required, className = "" }) {
  const testId = `contact-field-${name}`;
  const placeholder = label;
  const commonProps = {
    id: name,
    name,
    value,
    onChange,
    required,
    placeholder,
    "data-testid": testId,
    className:
      "mt-2 w-full px-4 py-3.5 rounded-2xl hairline glass bg-transparent text-sm outline-none focus:ring-2 focus:ring-brand-plum/40 placeholder:text-muted-foreground",
  };
  return (
    <label className={`block ${className}`} htmlFor={name}>
      <span className="text-xs font-mono tracking-[0.2em] uppercase text-muted-foreground">{label}</span>
      {as === "textarea" ? (
        <textarea rows={5} {...commonProps} />
      ) : (
        <input type={type} {...commonProps} />
      )}
    </label>
  );
}
